import { useSortable } from '@dnd-kit/react/sortable';
import { Button, Tooltip } from '@ibsys/kit';
import { GripVerticalIcon, SettingsIcon, Trash2Icon } from 'lucide-react';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { isResolvedChart, toChartDefinition, type PresetChartFormValues } from '../../model/dashboardPresetForm';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import {
  CHART_CELL_GUTTER_CLASS,
  CHART_ROW_START_CLASS,
  CHART_SIZE_COL_SPAN,
  chartCellStyle,
} from '../DashboardPresetGrid/chartLayout';
import { CustomChartCard, type CustomChartPanelFilters } from '../DashboardPresetGrid/CustomChartCard';
import { ChartResizeHandle } from './ChartResizeHandle';
import { NewChartPlaceholder } from './NewChartPlaceholder';
import { useChartCardResize } from './useChartCardResize';

type Props = {
  chart: PresetChartFormValues;
  /** Позиция диаграммы в дашборде — от неё считается перестановка перетаскиванием */
  index: number;
  /** Фильтры панели страницы: в режиме редактирования диаграммы наполнены теми же данными */
  filters: CustomChartPanelFilters;
  /** Свойства диаграммы развёрнуты блоком под её строкой */
  isPropertiesExpanded: boolean;
  /** Ширина сетки дашборда: по ней считается доля ширины при растягивании */
  getGridWidth: () => number;
  onToggleProperties: () => void;
  onRemove: () => void;
  onResize: (change: ChartSizeChange) => void;
};

/**
 * Диаграмма правящегося дашборда: показана своими данными, но взята в обвязку правки —
 * перетаскивание за ручку, растягивание границ, свойства и удаление.
 *
 * Ячейкой сетки диаграмма встаёт сама — той же долей ширины и с той же высотой, что и на
 * применённом дашборде: правка показывает дашборд таким, каким он получится, поэтому и в строке
 * диаграмма стоит наравне с соседками. Свойства раскрываются отдельным блоком под всей строкой.
 *
 * Данные берутся тем же запросом, что и у применённого дашборда, а размер в его ключ не входит:
 * растянутая диаграмма перестраивает уже полученное содержимое, не обращаясь к бэкенду заново.
 *
 * Заводимая диаграмма правила ещё не имеет: на её месте показана заготовка, а растягивания и
 * свойств у неё нет — задавать их нечему, пока описание не истолковано.
 */
export const EditableChartCard: FC<Props> = ({
  chart,
  index,
  filters,
  isPropertiesExpanded,
  getGridWidth,
  onToggleProperties,
  onRemove,
  onResize,
}) => {
  const { t } = useTranslation('qualityDashboard');
  const { ref, handleRef, isDragging } = useSortable({ id: chart.chartId, index });
  const cardRef = useRef<HTMLDivElement>(null);
  const { isResizing, handlePointerDown, handlePointerMove, handlePointerUp } = useChartCardResize({
    cardRef,
    getGridWidth,
    onResize,
  });
  const isResolved = isResolvedChart(chart);

  return (
    <div
      ref={ref}
      className={cn(
        'grid min-w-0',
        CHART_SIZE_COL_SPAN[chart.size],
        CHART_CELL_GUTTER_CLASS,
        chart.startsRow && CHART_ROW_START_CLASS,
        isDragging && 'opacity-50',
      )}
      style={chartCellStyle(chart.height)}
    >
      {/*
        Карточка стоит внутри отступов ячейки, а обвязка правки — по её границам: ручки и кнопки
        отсчитываются от самой карточки, а не от занятой диаграммой доли сетки вместе с зазором.
      */}
      <div
        className={cn(
          'group/chart relative grid min-h-0 min-w-0 overflow-hidden rounded-xl',
          isResizing && 'ring-2 ring-primary',
        )}
      >
        {/*
          Содержимое диаграммы живое и здесь: не поместившееся прокручивается внутри неё так же,
          как на применённом дашборде. Переходов к диалогам в режиме редактирования нет —
          диаграмма получает их незаданными и потому не показывает содержимое нажимаемым.
        */}
        <div ref={cardRef} className="grid min-h-0 min-w-0 overflow-hidden">
          {isResolvedChart(chart) ? (
            <CustomChartCard chart={toChartDefinition(chart)} filters={filters} />
          ) : (
            <NewChartPlaceholder />
          )}
        </div>

        <div className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md border bg-card/90 p-1 opacity-0 transition-opacity group-hover/chart:opacity-100 group-focus-within/chart:opacity-100">
          <div
            ref={(element) => handleRef(element)}
            role="button"
            tabIndex={-1}
            aria-label={t('presets.editor.dragChart')}
            className="cursor-grab px-1 text-muted-foreground"
          >
            <GripVerticalIcon className="size-4" />
          </div>
          {isResolved && (
            <Tooltip tooltip={t('presets.editor.chartProperties')}>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('presets.editor.chartProperties')}
                aria-pressed={isPropertiesExpanded}
                icon={<SettingsIcon className={cn('size-4', isPropertiesExpanded && 'text-primary')} />}
                onClick={onToggleProperties}
              />
            </Tooltip>
          )}
          <Tooltip tooltip={t('presets.editor.removeChart')}>
            <Button
              variant="ghost"
              size="icon"
              color="destructive"
              aria-label={t('presets.editor.removeChart')}
              icon={<Trash2Icon className="size-4" />}
              onClick={onRemove}
            />
          </Tooltip>
        </div>

        {isResolved && (
          <>
            <ChartResizeHandle
              edge="width"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
            <ChartResizeHandle
              edge="height"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            />
          </>
        )}
      </div>
    </div>
  );
};
